import type {
  ActivityFeedItem,
  InterviewRow,
  OfferRow,
  ProposalRow,
  ProposalStatus,
  InterviewStatus,
  InterviewType,
  OfferStatus,
} from '../data/clientEngagementMock';
import { isDirectWebsiteSollicitatie } from './candidateJourneyBuckets';
import {
  normalizeCvSnapshotForPortal,
  parsePortalClientProposal,
  parsePortalThreadFromCvSnapshot,
  type PortalClientProposalSnapshot,
} from './portalProposalThread';
import { formatPipelineInterviewHint } from './clientPipelineConsent';
import { includeInVacancyVoorgesteldClientList } from './vacancyClientPortalStage';

function initialsFromName(name: string): string {
  const p = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (p.length === 0) return '?';
  if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
  return (p[0][0] + p[p.length - 1][0]).toUpperCase();
}

function skillsFromCandidate(c: any): string[] {
  if (!c) return [];
  if (Array.isArray(c.skill_set)) return c.skill_set.map(String).filter(Boolean).slice(0, 14);
  if (typeof c.skill_set === 'string') {
    return c.skill_set
      .split(/[,;|]/)
      .map((s: string) => s.trim())
      .filter(Boolean)
      .slice(0, 14);
  }
  const ai = c.ai_parsed_data;
  if (ai?.skills && Array.isArray(ai.skills)) return ai.skills.map(String).filter(Boolean).slice(0, 14);
  if (ai?.tags && Array.isArray(ai.tags)) return ai.tags.map(String).filter(Boolean).slice(0, 14);
  return [];
}

function parseParsedCvObject(c: any): Record<string, unknown> | null {
  const p = c?.parsed_cv;
  if (!p) return null;
  if (typeof p === 'object' && !Array.isArray(p)) return p as Record<string, unknown>;
  if (typeof p === 'string') {
    try {
      const o = JSON.parse(p) as unknown;
      return o && typeof o === 'object' && !Array.isArray(o) ? (o as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  }
  return null;
}

function buildMotivationOriginal(c: any): string | null {
  const ai = c?.ai_parsed_data && typeof c.ai_parsed_data === 'object' ? c.ai_parsed_data : null;
  const fromAi = ai?.motivation != null ? String(ai.motivation).trim() : '';
  const parsed = parseParsedCvObject(c);
  const fromParsed = parsed?.motivation != null ? String(parsed.motivation).trim() : '';
  const t = fromAi || fromParsed;
  return t || null;
}

function buildMatchExplanation(
  app: any,
  c: any,
  portalProposal: PortalClientProposalSnapshot | null,
): string | null {
  const chunks: string[] = [];
  const seen = new Set<string>();
  const push = (raw: unknown) => {
    const t = String(raw || '').trim();
    if (!t || seen.has(t)) return;
    seen.add(t);
    chunks.push(t);
  };
  push(portalProposal?.match_notes);
  push(app?.match_reason);
  push(c?.ai_parsed_data?.match_reason);
  if (chunks.length === 0 && typeof app?.match_score === 'number' && app.match_score > 0) {
    return `Matchscore: ${Math.round(app.match_score)}%.`;
  }
  return chunks.length ? chunks.join('\n\n') : null;
}

/** Fallback voor zichtbare recruiter-samenvatting als er nog geen `portal_client_proposal.summary` is. */
function recruiterSummaryFrom(app: any, c: any): string {
  const parts: string[] = [];
  const seen = new Set<string>();
  const push = (raw: unknown) => {
    const t = String(raw || '').trim();
    if (!t || seen.has(t)) return;
    seen.add(t);
    parts.push(t);
  };
  push(app?.motivation);
  push(c?.cv_summary);
  const ai = c?.ai_parsed_data && typeof c.ai_parsed_data === 'object' ? c.ai_parsed_data : null;
  if (ai) {
    const o = ai as Record<string, unknown>;
    push(o.summary);
    push(o.cv_summary);
    push(o.profiel);
  }
  const joined = parts.join('\n\n').trim();
  if (joined) return joined.length > 4000 ? `${joined.slice(0, 3997)}…` : joined;
  const role = String(c?.role || '').trim();
  return role || '—';
}

/** Map pipeline string naar UI-badge (niet 1-op-1 met database-stadia). */
export function mapPipelineStatusToProposalStatus(status: string | null | undefined): ProposalStatus {
  const s = String(status || '').toLowerCase();
  if (/(afgewezen|reject|niet aangenomen|niet geplaatst|closed)/.test(s)) return 'rejected';
  if (/(shortlist|op shortlist)/.test(s)) return 'shortlisted';
  if (/(meer info|informatie.*gevraagd|vraag.*klant)/.test(s)) return 'more_info_requested';
  if (/(interview|gesprek|aanbod|offer)/.test(s)) return 'interview_requested';
  if (/(on hold|wacht|pauze|later)/.test(s)) return 'on_hold';
  if (/(voorgesteld|voorgedragen|cv|nieuw|screening|submitted|applied)/.test(s)) return 'waiting_feedback';
  return 'waiting_feedback';
}

export function applicationNeedsClientAttention(status: string | null | undefined): boolean {
  const ps = mapPipelineStatusToProposalStatus(status);
  return ps === 'waiting_feedback' || ps === 'more_info_requested';
}

export function proposalNeedsClientAttention(p: Pick<ProposalRow, 'pipelineStatus'>): boolean {
  return applicationNeedsClientAttention(p.pipelineStatus);
}

export function mapApplicationToProposalRow(app: any, clientId: string): ProposalRow {
  const v = app.vacancies || app.vacancy || {};
  const c = app.candidates || app.candidate || {};
  const pipeline = String(app.status || '');
  const ps = mapPipelineStatusToProposalStatus(pipeline);
  const cvPlain = c?.original_cv_text != null ? String(c.original_cv_text) : null;
  const cvSummary = c?.cv_summary != null ? String(c.cv_summary) : null;

  const snapObj = normalizeCvSnapshotForPortal(app?.cv_snapshot);
  const portalThread = parsePortalThreadFromCvSnapshot(app?.cv_snapshot);
  const portalProposal = parsePortalClientProposal(app?.cv_snapshot);
  const fromPortalMs =
    portalProposal && typeof portalProposal.match_score === 'number' && !Number.isNaN(portalProposal.match_score)
      ? Math.max(0, Math.min(100, Math.round(portalProposal.match_score)))
      : undefined;
  const matchScore =
    typeof app.match_score === 'number' && !Number.isNaN(app.match_score)
      ? Math.max(0, Math.min(100, Math.round(app.match_score)))
      : fromPortalMs ?? 0;
  const hasFormattedClientCv = !!(
    snapObj.portal_client_cv_formatted &&
    typeof snapObj.portal_client_cv_formatted === 'object'
  );

  const matchExplanation = buildMatchExplanation(app, c, portalProposal);
  const motivationOriginal = buildMotivationOriginal(c);
  const recruiterPortalSummary = portalProposal?.summary?.trim() || null;
  const recruiterSummaryDisplay = recruiterPortalSummary || recruiterSummaryFrom(app, c);
  const availabilityDisplay =
    portalProposal?.available_from || portalProposal?.hours_per_week
      ? [portalProposal.available_from, portalProposal.hours_per_week ? `${portalProposal.hours_per_week} u/w` : '']
          .filter(Boolean)
          .join(' · ') || String(c.availability || c?.ai_parsed_data?.availability || '—')
      : String(c.availability || c?.ai_parsed_data?.availability || '—');
  const salaryDisplay = portalProposal?.salary_indication?.trim()
    ? portalProposal.salary_indication.trim()
    : String(c.salary_range || c?.ai_parsed_data?.salary_expectation || '—');

  return {
    proposalId: String(app.id),
    isWebsiteInbound: isDirectWebsiteSollicitatie(app),
    id: String(c.id || app.candidate_id),
    name: String(c.name || 'Kandidaat'),
    initials: initialsFromName(c.name || ''),
    role: String(c.role || ''),
    vacancyTitle: String(v.title || 'Vacature'),
    vacancyId: String(app.vacancy_id || v.id),
    matchScore,
    status: ps,
    matchExplanation,
    recruiterPortalSummary,
    motivationOriginal,
    recruiterSummary: recruiterSummaryDisplay,
    skills: skillsFromCandidate(c),
    availability: availabilityDisplay,
    salaryIndication: salaryDisplay,
    submittedAt: String(app.created_at || new Date().toISOString()),
    lastUpdatedAt: String(app.updated_at || app.created_at || new Date().toISOString()),
    clientId: String(clientId),
    recruiterId: String(v.assigned_recruiter_id || ''),
    pipelineStatus: pipeline,
    cvPlainText: cvPlain,
    cvSummary,
    aiParsed: (c?.ai_parsed_data && typeof c.ai_parsed_data === 'object' ? c.ai_parsed_data : null) as Record<
      string,
      unknown
    > | null,
    candidateEmail: c?.email != null ? String(c.email).trim() || undefined : undefined,
    candidatePhone: c?.phone != null ? String(c.phone).trim() || undefined : undefined,
    candidateLinkedin: c?.linkedin_url != null ? String(c.linkedin_url).trim() || undefined : undefined,
    portalThread: portalThread.length ? portalThread : undefined,
    portalClientProposal: portalProposal,
    hasFormattedClientCv,
    portalCvFormatted: hasFormattedClientCv
      ? (snapObj.portal_client_cv_formatted as Record<string, unknown>)
      : null,
    showInClientVoorgesteldEngagement: includeInVacancyVoorgesteldClientList(app),
    scheduledInterviewHint: formatPipelineInterviewHint(snapObj.pipeline_meta as Record<string, unknown> | undefined),
  };
}

function mapInterviewDbStatus(s: string | undefined): InterviewStatus {
  const x = String(s || '').toLowerCase();
  if (/(complete|afgerond|done)/.test(x)) return 'completed';
  if (/(cancel|no.show|niet)/.test(x)) return 'completed';
  if (/(feedback|evaluat)/.test(x)) return 'feedback_needed';
  if (/(verplaats|resched|reschedulereq)/.test(x)) return 'reschedule_requested';
  return 'planned';
}

function mapInterviewType(row: any): InterviewType {
  const v = String(row?.video_link || '').trim();
  if (v) return 'online';
  const loc = String(row?.location || '').trim();
  if (loc && /http|teams|zoom|meet\.google/i.test(loc)) return 'online';
  if (loc) return 'locatie';
  return 'telefonisch';
}

export function mapInterviewRow(row: any, clientId: string): InterviewRow {
  const cand = Array.isArray(row?.candidates) ? row.candidates[0] : row?.candidates || {};
  const vac = Array.isArray(row?.vacancies) ? row.vacancies[0] : row?.vacancies || {};
  const names: string[] = [];
  if (Array.isArray(row.interviewers)) {
    row.interviewers.forEach((x: any) => {
      if (typeof x === 'string') names.push(x);
    });
  }
  return {
    id: String(row.id),
    candidateId: String(row.candidate_id),
    candidateName: String(cand.name || 'Kandidaat'),
    vacancyId: String(row.vacancy_id),
    vacancyTitle: String(vac.title || 'Vacature'),
    startsAt: String(row.scheduled_at || new Date().toISOString()),
    type: mapInterviewType(row),
    interviewers: names.length ? names : ['—'],
    status: mapInterviewDbStatus(row.status),
    clientId: String(clientId),
    recruiterId: String(vac.assigned_recruiter_id || ''),
  };
}

function mapPlacementToOfferStatus(st: string | undefined): OfferStatus {
  const s = String(st || '').toLowerCase();
  if (/(voltooid|afgerond|geplaatst|succes)/.test(s)) return 'accepted';
  if (/(afgewezen|reject|geannuleerd)/.test(s)) return 'draft';
  if (/(onderhandel|negotiat)/.test(s)) return 'negotiating';
  if (/(risico|risk)/.test(s)) return 'risk';
  if (/(goedgekeurd|approved|akkoord|actief)/.test(s)) return 'sent';
  return 'sent';
}

function riskFromPlacement(p: any): 'low' | 'medium' | 'high' {
  const r = p?.data?.risk_level || p?.data?.risk;
  if (r === 'high' || r === 'hoog') return 'high';
  if (r === 'medium' || r === 'midden') return 'medium';
  return 'low';
}

function salaryRangeFromPlacement(p: any): string {
  if (p?.salary != null && Number(p.salary) > 0) return `€ ${Number(p.salary).toLocaleString('nl-NL')} bruto/jaar`;
  if (p?.pay_rate != null && Number(p.pay_rate) > 0) return `€ ${Number(p.pay_rate).toLocaleString('nl-NL')} (tarief)`;
  if (p?.billing_rate != null && Number(p.billing_rate) > 0) return `€ ${Number(p.billing_rate).toLocaleString('nl-NL')} (billing)`;
  return '—';
}

export function mapPlacementToOfferRow(p: any, clientId: string): OfferRow {
  const cand = p.candidate || p.candidates || {};
  const vac = p.vacancy || p.vacancies || {};
  const endRaw = p.end_date != null ? String(p.end_date) : null;
  const updRaw = p.updated_at != null ? String(p.updated_at) : p.created_at != null ? String(p.created_at) : null;
  return {
    id: String(p.id),
    candidateId: String(p.candidate_id || cand.id || ''),
    candidateName: String(cand.name || 'Kandidaat'),
    vacancyId: String(p.vacancy_id || vac.id || ''),
    vacancyTitle: String(vac.title || 'Vacature'),
    salaryRange: salaryRangeFromPlacement(p),
    startDate: String(p.start_date || p.created_at || '').slice(0, 10),
    status: mapPlacementToOfferStatus(p.status),
    riskLevel: riskFromPlacement(p),
    clientId: String(clientId),
    recruiterId: String(p.recruiter_id || ''),
    endDateIso: endRaw && endRaw.length >= 8 ? endRaw : null,
    placementUpdatedAt: updRaw || null,
  };
}

/** Dagen tot/tot na een ISO-datum (negatief = verleden). */
export function daysFromIso(iso: string | null | undefined, fromMs: number = Date.now()): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.round((t - fromMs) / 86400000);
}

/** Opdracht eindigt binnen N weken (en nog niet verlopen meer dan een paar dagen). */
export function placementEndsWithinWeeks(off: Pick<OfferRow, 'endDateIso'>, weeks: number): boolean {
  const d = daysFromIso(off.endDateIso || null);
  if (d === null) return false;
  return d >= -7 && d <= weeks * 7;
}

/** Geen dossier-update op plaatsing sinds N maanden (proxy voor contact/notitie). */
export function placementStaleMonths(off: Pick<OfferRow, 'placementUpdatedAt'>, months: number): boolean {
  const d = daysFromIso(off.placementUpdatedAt || null);
  if (d === null) return false;
  return d <= -Math.round(months * 30);
}

/** Voorstel loopt bij de klant (niet meer “nieuw te beoordelen”, niet afgewezen). */
export function proposalIsLopendVoorstel(p: Pick<ProposalRow, 'status'>): boolean {
  if (p.status === 'rejected') return false;
  if (p.status === 'waiting_feedback') return false;
  return true;
}

/** Ruwe heuristiek op beschikbaarheidstekst (tot 1 maand). */
export function availabilityLooksWithinOneMonth(text: string | null | undefined): boolean {
  const raw = String(text || '').trim();
  if (!raw || raw === '—') return false;
  const t = raw.toLowerCase();
  if (/per\s*(vandaag|direct|meteen|zo snel)/.test(t)) return true;
  if (/binnen\s*(1|één|een)\s*maand/.test(t)) return true;
  const wm = t.match(/binnen\s*(\d+)\s*weken?/);
  if (wm && Number(wm[1]) <= 4) return true;
  const dm = t.match(/binnen\s*(\d+)\s*dagen?/);
  if (dm && Number(dm[1]) <= 31) return true;
  const iso = raw.match(/\d{4}-\d{2}-\d{2}/);
  if (iso) {
    const days = daysFromIso(iso[0]);
    if (days !== null && days >= 0 && days <= 31) return true;
  }
  return false;
}

export function buildActivityFromApplications(apps: ProposalRow[], limit = 25): ActivityFeedItem[] {
  const sorted = [...apps].sort(
    (a, b) => new Date(b.lastUpdatedAt).getTime() - new Date(a.lastUpdatedAt).getTime(),
  );
  return sorted.slice(0, limit).map((p, i) => ({
    id: `feed-${p.proposalId}-${i}-${p.lastUpdatedAt}`,
    icon: 'sparkles' as const,
    createdAt: p.lastUpdatedAt,
    actor: 'recruiter' as const,
    actorLabel: 'Pipeline',
    description: `${p.name} — ${p.pipelineStatus || 'Update'}`,
    candidateName: p.name,
    vacancyTitle: p.vacancyTitle,
  }));
}

/** Database-status na snelle actie (best-effort; pas aan naar je eigen Kanban-namen). */
export function quickActionToDbStatus(action: 'niet_passend' | 'later' | 'meer_info' | 'interview'): string {
  switch (action) {
    case 'niet_passend':
      return 'Afgewezen door opdrachtgever';
    case 'later':
      return 'On hold (klantportaal)';
    case 'meer_info':
      return 'Meer informatie (klantportaal)';
    case 'interview':
      return 'Interview gewenst (klantportaal)';
    default:
      return 'Voorgesteld';
  }
}

/** Ruwe pipeline-tekst from recruiter: “voorgesteld” aan de klant. */
export function isPipelineVoorgesteld(pipeline: string | null | undefined): boolean {
  return /voorgesteld|voorgedragen|voorstel|nieuw.*klant|ter goedkeuring/i.test(String(pipeline || ''));
}

export function interviewFollowupToApplicationStatus(kind: 'next_round' | 'reject'): string {
  if (kind === 'next_round') return 'Volgende ronde — akkoord klant (portaal)';
  return 'Afgewezen door opdrachtgever na gesprek';
}
